import { Button } from "@admin/components/atoms/Button";
import { Select } from "@admin/components/atoms/Input/Select";
import { Loading } from "@admin/components/atoms/Loading";
import { Tabs } from "@admin/components/atoms/Tabs";
import { useCurrentUserGroups } from "@admin/hooks/useCurrentUserGroups";
import { LocationInfo } from "@admin/pages/shift-management/components";
import { CompanyLocation, useCommonStore } from "@enos-monorepo/lib";
import { CompanySpecificDocument } from "@enos-monorepo/lib/gql/graphql";
import { hygraphClient } from "@enos-monorepo/lib/gql/hygraphClient";
import { useQuery } from "@tanstack/react-query";
import { lazy, useMemo } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { CompanySpecific } from "./CompanySpecific";
import { RiskAnalysis } from "./RiskAnalysis";

const ProgramContent = lazy(() => import("./ProgramContent"));

interface InfoBankLocationTabsProps {
  selectedLocation?: CompanyLocation;
  locations?: CompanyLocation[];
  date?: Date;
  isLoading?: boolean;
  companyId?: string;
}

export function InfoBankLocationTabs({
  selectedLocation,
  locations = [],
  date,
  isLoading,
}: InfoBankLocationTabsProps) {
  const { t } = useTranslation();
  const { setFoodLocation } = useCommonStore();
  const { isSuperAdmin } = useCurrentUserGroups();

  const { data: companySpecificData, isPending } = useQuery({
    queryKey: ["companySpecificData", selectedLocation?.id],
    queryFn: () =>
      hygraphClient.request(CompanySpecificDocument, {
        locationId: selectedLocation?.id ?? "",
      }),
    enabled: !!selectedLocation?.id,
  });

  const tabs = useMemo(() => {
    const items = [
      {
        label: t("riskAnalyses.Risk Analysis"),
        content: <RiskAnalysis locationId={selectedLocation?.id} />,
      },
      {
        label: t("riskAnalyses.Program Content"),
        content: <ProgramContent locationId={selectedLocation?.id} />,
      },
    ];
    if (companySpecificData?.companySpecific)
      items.push({
        label: t("riskAnalyses.Company Specific"),
        content: <CompanySpecific companySpecificData={companySpecificData} />,
      });

    return items;
  }, [companySpecificData, selectedLocation?.id, t]);

  if (isLoading || isPending) {
    return <Loading />;
  }

  return (
    <>
      <div className="flex flex-row rounded-xl bg-white justify-between p-7.5 pb-16 -mb-12">
        <LocationInfo selectedLocation={selectedLocation} date={date} />
        <div className="w-1/4 flex flex-row items-center gap-2">
          {isSuperAdmin && (
            <Button
              icon
              onClick={() => {
                navigator.clipboard
                  .writeText(selectedLocation?.id ?? "")
                  .then(() => {
                    toast.success(
                      `Location ID Copied to Clipboard ${selectedLocation?.id}`,
                    );
                  });
              }}
            >
              <i className="ki-outline ki-copy "></i>
            </Button>
          )}
          <Select
            defaultValue={selectedLocation?.id}
            options={locations.map((l) => {
              return { label: l.name, value: l.id };
            })}
            onChange={(e) => {
              setFoodLocation(e.target.value);
            }}
          />
        </div>
      </div>
      <Tabs items={tabs} />
    </>
  );
}
